import { useEffect, useState, useTransition } from 'react'
import { getBaseApi, getLocaleFromUrlOrQuery } from '../Utils/common'
import { format } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'
import { useTravelSearchContext } from '../context/TravelSearchProvider'
import { useRoutes } from './routes/useRoutes'
import { useVehicles } from './vehicles/useVehicles'
import { useTravelDates } from './dates/useTravelDates'
import { fetcher } from '../Utils/fetcher'

const baseApi = getBaseApi()

export function useBookingUrl(currency?: string, offercode?: string): UseBookingUrlResult {
  // Instead of local boolean, we use React concurrency
  const [isPending, startTransition] = useTransition()
  const [bookingUrl, setBookingUrl] = useState<string>('')

  const locale = getLocaleFromUrlOrQuery()
  const {
    state: { isOneWay },
  } = useTravelSearchContext()

  // gather from sub-hooks
  const { routesApiData, selectedRoute } = useRoutes()
  const { selectedVehicle, selectedPassengers, multipleVehicles } = useVehicles()
  const { selectedOutboundDate, selectedHomeboundDate } = useTravelDates()

  // build URL search params
  const routeCode = selectedRoute?.routeCode
  const vehicleCode = selectedVehicle?.code
  const trailerCode = selectedVehicle?.trailer?.code
  const multipleVehiclesChecked = multipleVehicles
  const vehicleCount = selectedVehicle?.count

  const adults = selectedPassengers?.adults ?? 1
  const children = selectedPassengers?.children ?? 0
  const childAges = selectedPassengers?.childrenAges ?? []
  const pets = selectedPassengers?.pets ?? 0

  const outboundDate = selectedOutboundDate
  const homeboundDate = selectedHomeboundDate

  let offerOrProductCode = routesApiData?.offerOrProductCode
  if (!offerOrProductCode && offercode) {
    offerOrProductCode = offercode
  }

  const params = new URLSearchParams()
  locale && params.append('locale', locale)
  routeCode && params.append('routeCode', routeCode)
  vehicleCode && params.append('vehicleCode', vehicleCode)
  outboundDate && params.append('outboundDate', format(outboundDate, 'yyyy-MM-dd'))
  params.append('isOneway', isOneWay ? 'true' : 'false')
  params.append('adults', adults.toString())
  offerOrProductCode && params.append('offerOrProductCode', offerOrProductCode)
  trailerCode && params.append('trailerCode', trailerCode)
  homeboundDate && params.append('homeboundDate', format(homeboundDate, 'yyyy-MM-dd'))
  if (children > 0) {
    params.append('children', children.toString())
    childAges.forEach((age: number) => params.append('childAges', age.toString()))
  }
  if (pets > 0) params.append('pets', pets.toString())
  if (currency) params.append('currency', currency)
  if (multipleVehiclesChecked) params.append('multipleVehicles', 'true')
  if (vehicleCount && vehicleCount > 1) params.append('numberOfVehicles', vehicleCount.toString())

  const { sessionId } = setSession()
  if (sessionId) params.append('sessionId', sessionId)

  const bookingUrlApi = `${baseApi}/booking-url?${params.toString()}`

  useEffect(() => {
    if (!outboundDate || (!isOneWay && !homeboundDate)) return
    let canceled = false

    startTransition(() => {
      fetcher<BookingResponse>(bookingUrlApi)
        .then((data) => {
          if (canceled) return

          const origin = window.location.origin
          const finalUrl = isVercelUrl(window.location.href)
            ? `${isVercelUrl(window.location.href)}/${data?.url}`
            : `${origin}/${data?.url}`

          // If it's an FRS route, data.url is used raw
          const maybeFrsUrl = data?.isFrsRoute ? data.url : finalUrl
          const finalWithSrc = addSrcParamIfItExists(window.location.href, maybeFrsUrl)
          setBookingUrl(finalWithSrc)
        })
        .catch((err) => {
          console.error('Error fetching booking url:', err)
          setBookingUrl('')
        })
    })

    return () => {
      canceled = true
    }
  }, [bookingUrlApi])

  return {
    isLoading: isPending,
    url: bookingUrl,
  }
}

export interface UseBookingUrlResult {
  isLoading: boolean
  url: string
}

interface BookingResponse {
  url: string
  isFrsRoute?: boolean
}

// Utility checks
function isVercelUrl(url: string): string | undefined {
  if (url.includes('localhost:5001')) return 'https://localhost:5001'
  if (url.includes('travel-search-dev')) return 'https://www.dev.dfds.com'
  else if (url.includes('app-travel-search-nonprod') || url.includes('pax-travel-search') || url.includes('travel-search-test'))
    return 'https://www.test.dfds.com'
  else if (url.includes('travel-search-uat')) return 'https://www.accept.dfds.com'
  else if (url.includes('app-travel-search-prod') || url.includes('travel-search-prod')) return 'https://www.dfds.com'
  else return undefined
}

function addSrcParamIfItExists(srcUrl: string, targetUrl: string): string {
  const srcUrlObj = new URL(srcUrl)
  const targetUrlObj = new URL(targetUrl)

  const srcParam = srcUrlObj.searchParams.get('utm_source')
  if (srcParam) {
    targetUrlObj.searchParams.set('utm_source', srcParam)
  }

  return targetUrlObj.toString()
}

// Session logic
const SESSION = 'session'
function setSession(): { sessionId: string } {
  const sessionIdFromSS = getSession()?.sessionId
  const sessionId = sessionIdFromSS || uuidv4()
  sessionStorage.setItem(SESSION, JSON.stringify({ sessionId }))
  return { sessionId }
}

export function getSession(): { sessionId: string } | undefined {
  return sessionStorage[SESSION] && JSON.parse(sessionStorage[SESSION])
}
